'use client';

import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout';
import { useWbsStore } from '@/stores/wbsStore';
import Dashboard from '@/components/wbs/Dashboard';
import TaskTable from '@/components/wbs/TaskTable';
import TaskFilters from '@/components/wbs/TaskFilters';
import TaskModal from '@/components/wbs/TaskModal';
import GanttChart from '@/components/wbs/GanttChart';
import KanbanBoard from '@/components/wbs/KanbanBoard';
import { dashboardApi, DashboardExtended } from '@/lib/api';
import type { Task } from '@/types';

export default function WbsPage() {
  const { refresh, isLoading } = useWbsStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [parentTaskForNew, setParentTaskForNew] = useState<Task | null>(null);
  const [projectDates, setProjectDates] = useState<{ start: string; end: string } | null>(null);
  const [viewType, setViewType] = useState<'table' | 'kanban'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('wbs_view_type') as 'table' | 'kanban') || 'table';
    }
    return 'table';
  });

  const handleViewChange = (view: 'table' | 'kanban') => {
    setViewType(view);
    localStorage.setItem('wbs_view_type', view);
  };

  useEffect(() => {
    refresh();
    // 프로젝트 날짜 가져오기
    dashboardApi.getExtended().then((data: DashboardExtended) => {
      if (data.project) {
        setProjectDates({
          start: data.project.start_date,
          end: data.project.end_date,
        });
      }
    }).catch(() => {
      // 프로젝트가 없으면 무시
    });
  }, [refresh]);

  const handleAddTask = () => {
    setEditingTask(null);
    setParentTaskForNew(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setParentTaskForNew(null);
    setIsModalOpen(true);
  };

  // 2뎁스 행의 + 버튼: 해당 stage 아래 3뎁스로 추가
  const handleAddSubTask = (parentTask: Task) => {
    setEditingTask(null);
    setParentTaskForNew(parentTask);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
    setParentTaskForNew(null);
  };

  return (
    <MainLayout>
      <div className="h-full flex flex-col overflow-hidden">
        {/* 상단 고정 영역 */}
        <div className="flex-shrink-0 w-full px-4 py-6">
          {/* 페이지 타이틀 */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">WBS</h2>
            <p className="text-gray-500 mt-1">프로젝트 작업 분류 체계를 관리합니다.</p>
          </div>

          {/* 대시보드 카드 */}
          <Dashboard />

          {/* 액션 버튼 영역 */}
          <TaskFilters onAddTask={handleAddTask} viewType={viewType} onViewChange={handleViewChange} />
        </div>

        {/* WBS 메인 영역 - 남은 공간 채우기 */}
        <section className="flex-1 mx-4 mb-4 bg-white rounded-xl shadow-md overflow-hidden min-h-0">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-500">데이터 로딩 중...</p>
            </div>
          ) : viewType === 'kanban' ? (
            <KanbanBoard onEditTask={handleEditTask} />
          ) : (
            <div className="flex h-full">
              {/* 좌측 고정 테이블 */}
              <div className="flex-shrink-0 border-r border-gray-200 overflow-auto" style={{ width: '50%', minWidth: '500px' }}>
                <TaskTable onEditTask={handleEditTask} onAddSubTask={handleAddSubTask} />
              </div>

              {/* 우측 간트차트 영역 */}
              <div className="flex-1 overflow-auto" style={{ width: '50%' }}>
                <GanttChart
                  onEditTask={handleEditTask}
                  projectStart={projectDates?.start}
                  projectEnd={projectDates?.end}
                />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        task={editingTask}
        parentTask={parentTaskForNew}
      />
    </MainLayout>
  );
}
